<script setup lang="ts">
import {
	getContextualHeaderClasses,
	useContextualHeaderAnimation
} from '~/composables/useContextualHeaderAnimation';
import { DependencyInjectionSymbols } from '~/core/helpers/symbols';
import { useNavigationStore } from '~/store/navigationStore';
import { cn } from '~/utils';

import { NavigationSection } from '~/components/NavigationSection';

const navigationStore = useNavigationStore();
const { isHeaderVisible, headerVariant, isContextualHeaderVisible } =
	storeToRefs(navigationStore);

const isMenuBeingShown = ref<boolean>(false);

const {
	shouldContextualBeVisible,
	shouldHeaderBeVisible,
	wrapperClasses,
	portalHeaderClasses
} = useContextualHeaderAnimation(
	isContextualHeaderVisible,
	isHeaderVisible,
	headerVariant
);

const contextualHeaderClasses = computed<string>(() => {
	return getContextualHeaderClasses(
		shouldContextualBeVisible.value,
		isMenuBeingShown.value
	);
});

provide(DependencyInjectionSymbols.isMenuBeingShown, isMenuBeingShown);

function handleNavOptionBeingShown() {
	isMenuBeingShown.value = true;
}

function handleNavOptionClosed() {
	isMenuBeingShown.value = false;
}
</script>
<template>
	<div class="min-h-screen" data-allow-mismatch="children">
		<div
			:class="
				cn(wrapperClasses, {
					'divide-y divide-primary-light-4': isContextualHeaderVisible
				})
			"
		>
			<div :class="portalHeaderClasses">
				<PortalHeader
					data-fixed-header="portal"
					variant="primary"
					class="h-[60px] md:h-20"
					:is-visible="shouldHeaderBeVisible"
					:has-header-overlay="true"
					@nav-option-being-shown="handleNavOptionBeingShown"
					@nav-option-closed="handleNavOptionClosed"
				/>
			</div>
			<div :class="contextualHeaderClasses">
				<ContextualHeader
					data-fixed-header="contextual"
					:is-visible="isContextualHeaderVisible"
				/>
			</div>
		</div>
		<main class="box-border size-full pt-[60px] md:pt-20">
			<slot />
		</main>

		<Container class="bg-primary-light-4 py-0">
			<NavigationSection />
		</Container>
		<Container class="bg-primary py-0!">
			<PortalFooter />
		</Container>
	</div>
</template>
